import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, CheckCircle2, Zap, MessageSquare, CheckSquare, FileText } from "lucide-react";

export default function Telegram() {
  const [integration, setIntegration] = useState({ status: "disconnected", config: {} });
  const [inbox, setInbox] = useState([]);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [message, setMessage] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations").then((r) => setIntegration(r.data.telegram));
  const loadInbox = () => api.get("/integrations/telegram/inbox").then((r) => setInbox(r.data)).catch(() => {});
  useEffect(() => { load(); loadInbox(); }, []);

  const connect = async () => {
    if (!botToken.trim()) return toast.error("Enter a bot token");
    setBusy(true);
    try {
      await api.post("/integrations/telegram/connect", {
        bot_token: botToken, chat_id: chatId, public_url: process.env.REACT_APP_BACKEND_URL,
      });
      toast.success("Telegram bot connected & webhook live");
      setBotToken("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => { await api.post("/integrations/telegram/disconnect", {}); load(); };

  const sendTest = async () => {
    if (!testMsg.trim()) return;
    try {
      await api.post("/integrations/telegram/send", { text: testMsg });
      toast.success("Message sent to Telegram");
      setTestMsg("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const toTask = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/agent/extract-tasks", { text: message, source: "telegram" });
      const n = data.payload?.tasks?.length || 0;
      toast.success(data.status === "executed" ? `${n} tasks created from message` : `${n} tasks drafted — approve in Copilot`);
      setMessage("");
    } finally {
      setBusy(false);
    }
  };

  const connected = integration.status === "connected";

  return (
    <div className="space-y-6" data-testid="telegram-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-slate-500">Channel</div>
          <h1 className="text-4xl font-black tracking-tighter">Telegram</h1>
        </div>
        {connected && (
          <span className="flex items-center gap-1.5 label-mono text-green-700 bg-green-100 px-3 py-1.5 rounded-sm">
            <CheckCircle2 size={14} /> Connected
          </span>
        )}
      </div>

      <Card className="rounded-sm border-border shadow-none p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#229ED9] text-white rounded-sm flex items-center justify-center"><Send size={20} /></div>
          <div>
            <h3 className="text-xl font-bold tracking-tight">Bot Assistant</h3>
            <p className="text-sm text-slate-600">Connect your Telegram bot for notifications and message operations.</p>
          </div>
        </div>
        {!connected ? (
          <div className="space-y-3 max-w-lg">
            <div>
              <label className="label-mono text-slate-500">Bot Token (from @BotFather)</label>
              <Input value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder="123456:ABC-DEF…" className="rounded-sm" data-testid="telegram-token-input" />
            </div>
            <div>
              <label className="label-mono text-slate-500">Chat ID (optional)</label>
              <Input value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="Your chat / channel id" className="rounded-sm" />
            </div>
            <Button onClick={connect} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="telegram-connect-button">Connect Bot</Button>
            <p className="text-xs text-muted-foreground">Token is stored securely and masked. Live message delivery activates once configured.</p>
          </div>
        ) : (
          <div className="space-y-4 max-w-lg">
            <div className="text-sm">
              <span className="text-slate-600">Bot: </span>
              <span className="font-mono font-semibold">@{integration.config?.bot_username}</span>
              <span className="text-slate-600 ml-3">Chat: </span>
              <span className="font-mono">{integration.config?.chat_id || "send a message to your bot first"}</span>
            </div>
            <div className="flex gap-2">
              <Input value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="Send a test message to your chat…" className="rounded-sm" data-testid="telegram-test-input" />
              <Button onClick={sendTest} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="telegram-send-button"><Send size={16} /></Button>
            </div>
            <Button variant="outline" onClick={disconnect} className="rounded-sm" data-testid="telegram-disconnect-button">Disconnect</Button>
            <p className="text-xs text-muted-foreground">Message your bot on Telegram — any message becomes a task. Use /task or /note for explicit capture.</p>
          </div>
        )}
      </Card>

      {connected && (
        <Card className="rounded-sm border-border shadow-none p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold tracking-tight">Inbox</h3>
            <button onClick={loadInbox} className="label-mono text-ai">Refresh</button>
          </div>
          {inbox.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages yet. Send a message to @{integration.config?.bot_username} on Telegram.</p>
          ) : (
            <div className="space-y-2" data-testid="telegram-inbox">
              {inbox.map((m) => (
                <div key={m.id} className="border-b border-border last:border-0 py-2">
                  <div className="text-sm">{m.text}</div>
                  <div className="label-mono text-slate-400 mt-0.5">{m.from} · {new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="rounded-sm border-border shadow-none p-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={18} className="text-ai" />
          <h3 className="text-xl font-bold tracking-tight">Bot Command Center</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">Your bot understands natural language and voice notes — or use these commands. Send anything and it acts on your workspace.</p>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5" data-testid="bot-commands">
          {[
            ["💬 Any message / voice note", "AI captures it as a task, note, or event — or answers"],
            ["/today", "Today's tasks & calendar"],
            ["/tasks  ·  /priorities", "List open / top-priority tasks"],
            ["/task <text>", "Add a task (with ✅ done button)"],
            ["/done <keyword>", "Complete a matching task"],
            ["/note <text>", "Save a note"],
            ["/event <when + what>", "AI parses date/time & schedules"],
            ["/projects", "Projects with progress"],
            ["/plan  ·  /brief", "AI daily plan / executive briefing"],
            ["/ask <question>", "Ask the AI copilot anything"],
            ["/search <query>", "Search tasks & notes"],
            ["/extract <text>", "Extract tasks → approve inline"],
          ].map(([c, d]) => (
            <div key={c} className="flex flex-col py-1.5 border-b border-border/60">
              <span className="font-mono text-sm text-slate-900">{c}</span>
              <span className="text-xs text-slate-500">{d}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm border-border shadow-none p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={18} className="text-slate-400" />
          <h3 className="text-xl font-bold tracking-tight">Message → Action</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">Paste a Telegram message or thread and convert it into tasks instantly.</p>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Paste a message or thread here…" className="rounded-sm min-h-[120px]" data-testid="telegram-message-input" />
        <Button onClick={toTask} disabled={busy} className="rounded-sm bg-ai hover:bg-blue-800 mt-3" data-testid="telegram-to-task-button">
          <CheckSquare size={16} className="mr-2" /> {busy ? "Processing…" : "Convert to Tasks"}
        </Button>
      </Card>
    </div>
  );
}
